import { Button, useConfirmModal } from '@affine/component';
import { SeatAddonService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { type I18nString, useI18n } from '@affine/i18n';
import { CloseIcon, InformationFillDuotoneIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { type WorkspaceMetadata, WorkspacesService } from '../../workspace';
import { WorkspaceQuotaService } from '../services/quota';
import * as styles from './styles.css';

interface Message {
  title: I18nString;
  description: I18nString;
  confirmText: I18nString;
  tips?: I18nString[];
  cancelText?: I18nString;
}

const readDismissedMemberOverageKey = (key: string | null): string | null => {
  if (!key) {
    return null;
  }
  try {
    return globalThis.localStorage?.getItem(key) === '1' ? key : null;
  } catch {
    return null;
  }
};

const writeDismissedMemberOverageKey = (key: string) => {
  try {
    globalThis.localStorage?.setItem(key, '1');
  } catch {
    // Ignore blocked storage. The in-memory state still dismisses this render.
  }
};

/**
 *
 * Notification that the cloud workspace quota has exceeded the limit
 *
 */
export const QuotaCheck = ({
  workspaceMeta,
}: {
  workspaceMeta: WorkspaceMetadata;
}) => {
  const { openConfirmModal } = useConfirmModal();
  const workspacesService = useService(WorkspacesService);
  const workspaceQuota = useService(WorkspaceQuotaService).quota;
  const seatAddonService = useService(SeatAddonService);
  const canPurchaseSeats = useLiveData(
    seatAddonService.seatAddon.canPurchaseSeats$
  );
  const workspaceProfile = workspacesService.getProfile(workspaceMeta);
  const quota = useLiveData(workspaceQuota.quota$);
  const usedPercent = useLiveData(workspaceQuota.percent$);
  const profile = useLiveData(workspaceProfile.profile$);
  const isOwner = profile?.isOwner;
  const isOwnerOrAdmin = !!profile?.isOwner || !!profile?.isAdmin;
  const workspaceDialogService = useService(WorkspaceDialogService);
  const dialog = useLiveData(workspaceDialogService.dialogs$);
  const t = useI18n();
  const memberOverflow = (quota?.overcapacityMemberCount ?? 0) > 0;
  const storageOverflow = !!usedPercent && usedPercent >= 100;
  const memberOverageDismissKey =
    workspaceMeta.flavour === 'local' || !quota || !memberOverflow
      ? null
      : [
          'streamlain',
          'quota',
          'member-overage-banner',
          workspaceMeta.id,
          quota.memberCount,
          quota.memberLimit,
          quota.overcapacityMemberCount,
        ].join(':');
  const [dismissedMemberOverageKey, setDismissedMemberOverageKey] = useState<
    string | null
  >(() => readDismissedMemberOverageKey(memberOverageDismissKey));
  const isMemberOverageDismissed =
    dismissedMemberOverageKey === memberOverageDismissKey ||
    readDismissedMemberOverageKey(memberOverageDismissKey) ===
      memberOverageDismissKey;

  useEffect(() => {
    if (isOwner) {
      seatAddonService.seatAddon.revalidate();
    }
  }, [isOwner, seatAddonService]);

  const onConfirm = useCallback(() => {
    if (!isOwner) {
      return;
    }
    if (
      dialog.some(
        d =>
          (d.type === 'setting' && d.props.activeTab === 'workspace:plans') ||
          (d.type === 'setting' && d.props.activeTab === 'workspace:billing')
      )
    ) {
      return;
    }
    workspaceDialogService.open('setting', {
      activeTab: canPurchaseSeats ? 'workspace:billing' : 'workspace:plans',
      scrollAnchor: canPurchaseSeats ? undefined : 'cloudPricingPlan',
    });
  }, [canPurchaseSeats, dialog, isOwner, workspaceDialogService]);

  const onMemberOverageAction = useCallback(() => {
    if (!isOwnerOrAdmin) {
      return;
    }
    const activeTab = isOwner
      ? canPurchaseSeats
        ? 'workspace:billing'
        : 'workspace:plans'
      : 'workspace:members';
    if (
      dialog.some(d => d.type === 'setting' && d.props.activeTab === activeTab)
    ) {
      return;
    }
    workspaceDialogService.open('setting', {
      activeTab,
      scrollAnchor:
        isOwner && !canPurchaseSeats ? 'cloudPricingPlan' : undefined,
    });
  }, [
    canPurchaseSeats,
    dialog,
    isOwner,
    isOwnerOrAdmin,
    workspaceDialogService,
  ]);

  const onDismissMemberOverage = useCallback(() => {
    if (!memberOverageDismissKey) {
      return;
    }
    writeDismissedMemberOverageKey(memberOverageDismissKey);
    setDismissedMemberOverageKey(memberOverageDismissKey);
  }, [memberOverageDismissKey]);

  useEffect(() => {
    workspaceQuota?.revalidate();
  }, [workspaceQuota]);

  useEffect(() => {
    if (!memberOverageDismissKey) {
      setDismissedMemberOverageKey(null);
      return;
    }
    setDismissedMemberOverageKey(
      readDismissedMemberOverageKey(memberOverageDismissKey)
    );
  }, [memberOverageDismissKey]);

  useEffect(() => {
    if (workspaceMeta.flavour === 'local' || !quota) {
      return;
    }
    if (memberOverflow && !storageOverflow) {
      return;
    }
    const message = getSyncPausedMessage(
      !!isOwner,
      memberOverflow,
      storageOverflow,
      !!canPurchaseSeats
    );

    if (memberOverflow || storageOverflow) {
      openConfirmModal({
        title: <Title title={t.t(message.title)} />,
        description: t.t(message.description),
        confirmText: t.t(message.confirmText),
        cancelText: message.cancelText ? t.t(message.cancelText) : undefined,
        children: <Tips tips={message.tips} />,
        childrenContentClassName: styles.modalChildren,
        onConfirm: onConfirm,
        confirmButtonOptions: {
          variant: 'primary',
        },
        cancelButtonOptions: {
          style: {
            visibility: message.cancelText ? 'visible' : 'hidden',
          },
        },
      });
      return;
    } else {
      return;
    }
  }, [
    canPurchaseSeats,
    isOwner,
    memberOverflow,
    onConfirm,
    openConfirmModal,
    quota,
    storageOverflow,
    t,
    workspaceMeta.flavour,
  ]);
  if (
    memberOverflow &&
    !storageOverflow &&
    isOwnerOrAdmin &&
    !isMemberOverageDismissed
  ) {
    return (
      <MemberOverageBanner
        canPurchaseSeats={!!canPurchaseSeats}
        isOwner={!!isOwner}
        overcapacityMemberCount={quota?.overcapacityMemberCount ?? 0}
        onAction={onMemberOverageAction}
        onDismiss={onDismissMemberOverage}
      />
    );
  }

  return null;
};

const memberOverageKeys = {
  title: 'com.streamlain.payment.member-overage.banner.title' as I18nString,
  ownerDescription:
    'com.streamlain.payment.member-overage.banner.owner.description' as I18nString,
  adminDescription:
    'com.streamlain.payment.member-overage.banner.admin.description' as I18nString,
  ownerSeatsAction:
    'com.streamlain.payment.member-overage.banner.owner.seats-action' as I18nString,
  ownerPlanAction:
    'com.streamlain.payment.member-overage.banner.owner.plan-action' as I18nString,
  adminAction:
    'com.streamlain.payment.member-overage.banner.admin.action' as I18nString,
  dismiss: 'com.streamlain.payment.member-overage.banner.dismiss' as I18nString,
};

const MemberOverageBanner = ({
  canPurchaseSeats,
  isOwner,
  overcapacityMemberCount,
  onAction,
  onDismiss,
}: {
  canPurchaseSeats: boolean;
  isOwner: boolean;
  overcapacityMemberCount: number;
  onAction: () => void;
  onDismiss: () => void;
}) => {
  const t = useI18n();
  return (
    <div className={styles.memberOverageBanner} role="status">
      <InformationFillDuotoneIcon className={styles.memberOverageIcon} />
      <div className={styles.memberOverageContent}>
        <div className={styles.memberOverageTitle}>
          {t.t(memberOverageKeys.title)}
        </div>
        <div className={styles.memberOverageDescription}>
          {t.t(
            isOwner
              ? memberOverageKeys.ownerDescription
              : memberOverageKeys.adminDescription,
            { count: String(overcapacityMemberCount) }
          )}
        </div>
      </div>
      <Button variant="primary" onClick={onAction}>
        {t.t(
          isOwner
            ? canPurchaseSeats
              ? memberOverageKeys.ownerSeatsAction
              : memberOverageKeys.ownerPlanAction
            : memberOverageKeys.adminAction
        )}
      </Button>
      <button
        type="button"
        className={styles.memberOverageDismiss}
        aria-label={t.t(memberOverageKeys.dismiss)}
        onClick={onDismiss}
      >
        <CloseIcon />
      </button>
    </div>
  );
};

const messages: Record<
  'owner' | 'member',
  Record<'both' | 'storage' | 'member', Message>
> = {
  owner: {
    both: {
      title: 'com.streamlain.payment.sync-paused.title',
      description: 'com.streamlain.payment.sync-paused.owner.both.description',
      tips: [
        'com.streamlain.payment.sync-paused.owner.both.tips-1',
        'com.streamlain.payment.sync-paused.owner.both.tips-2',
      ],
      cancelText: 'com.streamlain.payment.billing-setting.cancel',
      confirmText: 'com.streamlain.payment.upgrade',
    },
    storage: {
      title: 'com.streamlain.payment.sync-paused.title',
      description:
        'com.streamlain.payment.sync-paused.owner.storage.description',
      tips: [
        'com.streamlain.payment.sync-paused.owner.storage.tips-1',
        'com.streamlain.payment.sync-paused.owner.storage.tips-2',
      ],
      cancelText: 'com.streamlain.payment.billing-setting.cancel',
      confirmText: 'com.streamlain.payment.upgrade',
    },
    member: {
      title: 'com.streamlain.payment.sync-paused.title',
      description:
        'com.streamlain.payment.sync-paused.owner.member.description',
      tips: [
        'com.streamlain.payment.sync-paused.owner.member.tips-1',
        'com.streamlain.payment.sync-paused.owner.member.tips-2',
      ],
      cancelText: 'com.streamlain.payment.billing-setting.cancel',
      confirmText: 'com.streamlain.payment.upgrade',
    },
  },
  member: {
    both: {
      title: 'com.streamlain.payment.sync-paused.title',
      description: 'com.streamlain.payment.sync-paused.member.both.description',
      confirmText: 'com.streamlain.payment.sync-paused.member.member.confirm',
    },
    storage: {
      title: 'com.streamlain.payment.sync-paused.title',
      description:
        'com.streamlain.payment.sync-paused.member.storage.description',
      confirmText: 'com.streamlain.payment.sync-paused.member.member.confirm',
    },
    member: {
      title: 'com.streamlain.payment.sync-paused.title',
      description:
        'com.streamlain.payment.sync-paused.member.member.description',
      confirmText: 'com.streamlain.payment.sync-paused.member.member.confirm',
    },
  },
};

function getSyncPausedMessage(
  isOwner: boolean,
  isMemberOverflow: boolean,
  isStorageOverflow: boolean,
  canPurchaseSeats: boolean
): Message {
  // When seats can be purchased and it's a member-only overflow, show seat-specific message
  if (isOwner && canPurchaseSeats && isMemberOverflow && !isStorageOverflow) {
    return {
      title: 'com.streamlain.payment.sync-paused.title',
      description:
        'com.streamlain.payment.sync-paused.owner.member-seats.description',
      tips: [
        'com.streamlain.payment.sync-paused.owner.member-seats.tips-1',
        'com.streamlain.payment.sync-paused.owner.member-seats.tips-2',
      ],
      cancelText: 'com.streamlain.payment.billing-setting.cancel',
      confirmText:
        'com.streamlain.payment.sync-paused.owner.member-seats.confirm',
    };
  }

  const userType = isOwner ? 'owner' : 'member';
  const condition =
    isStorageOverflow && isMemberOverflow
      ? 'both'
      : isStorageOverflow
        ? 'storage'
        : isMemberOverflow
          ? 'member'
          : 'both';

  return messages[userType][condition];
}

const Tips = ({ tips }: { tips?: I18nString[] }) => {
  const t = useI18n();
  if (!tips || tips.length < 1) {
    return null;
  }
  return (
    <div className={styles.tipsStyle}>
      {tips.map(tip => (
        <div key={tip.toString()} className={styles.tipStyle}>
          <div className={styles.bullet} />
          {t.t(tip)}
        </div>
      ))}
    </div>
  );
};

const Title = ({ title }: { title: string }) => {
  return (
    <div className={styles.modalTitle}>
      <InformationFillDuotoneIcon className={styles.errorIcon} />
      {title}
    </div>
  );
};
